/**
 * Modal Field Adapter for Manufacturers
 * Transforms Manufacturer Field Registry into Modal field configurations
 */

import { FieldConfig } from '../../../components/modals/ModalFieldRenderer';
import { Manufacturer } from '../../../services/api/domains/manufacturers/types';
import { getFieldsForSurface } from '../fields';
import {
  BaseFieldDefinition,
  isDateOnlyField,
} from '../../../shared/types/fieldRegistry';
import { validationHelpers } from '../../../components/modals/utils/validation';
import { formatDateOnly, formatTimestamp } from '../../../utils/dateConversion';

/**
 * Convert a basic field definition to modal field config
 */
function createStandardField(
  field: BaseFieldDefinition
): FieldConfig<Manufacturer> {
  // Determine field type
  let fieldType: 'text' | 'textarea' | 'number' | 'date' | 'readonly' = 'text';

  if (field.isReadOnly) {
    fieldType = 'readonly';
  } else if (field.renderConfig?.rows && field.renderConfig.rows > 1) {
    fieldType = 'textarea';
  } else if (field.dataType === 'number') {
    fieldType = 'number';
  } else if (field.dataType === 'date') {
    fieldType = 'date';
  }

  const config: FieldConfig<Manufacturer> = {
    key: field.key as keyof Manufacturer,
    label: field.label,
    type: fieldType,
    getValue: (m: Manufacturer) => {
      const value = m[field.key as keyof Manufacturer];
      return value || '';
    },
    setValue: field.isReadOnly
      ? undefined
      : (m: Manufacturer, val: unknown) => ({
          ...m,
          [field.key]: val || undefined,
        }),
    isAlwaysDisabled: field.isReadOnly,
    isRequired: field.isRequired,
    tooltipText: field.description,
    rows: field.renderConfig?.rows,
  };

  // Add formatter for date fields
  if (field.dataType === 'date' && field.isReadOnly) {
    config.formatter = isDateOnlyField(field)
      ? formatDateOnly
      : formatTimestamp;
  }

  // Add validation for required fields
  if (field.isRequired && field.validation) {
    const validators = [];
    validators.push(validationHelpers.required);
    if (field.validation.minLength) {
      validators.push(validationHelpers.minLength(field.validation.minLength));
    }
    config.validator = validationHelpers.combine(...validators);
  }

  return config;
}

/**
 * Convert Manufacturer Field Registry to Modal field configurations
 * @returns Array of FieldConfig for Modal
 */
export function toModalFields(): FieldConfig<Manufacturer>[] {
  const fields: FieldConfig<Manufacturer>[] = [];

  for (const field of getFieldsForSurface('modal')) {
    // Skip computed fields (not editable)
    if (field.isComputed) {
      continue;
    }

    // 'facilities'/'products' never reach here — dropped from the 'modal'
    // surface (Task 10, Sub-tasks 10.4/10.5), superseded by
    // ProductsPanel.tsx/FacilitiesPanel.tsx.

    // Handle standard fields (string, number, date, readonly)
    if (field.dataType !== 'array' && field.dataType !== 'object') {
      fields.push(createStandardField(field));
    }
  }

  return fields;
}

/**
 * Get always disabled field names for manufacturers
 */
export function getAlwaysDisabledFields(): string[] {
  return getFieldsForSurface('modal')
    .filter(f => f.isReadOnly)
    .map(f => f.key as string);
}

/**
 * Get manufacturer ID field for display in view mode
 */
export function getManufacturerIdField(): FieldConfig<Manufacturer> {
  return {
    key: 'manufacturer_id',
    label: 'Manufacturer ID',
    type: 'text',
    getValue: m => m.manufacturer_id?.toString() || '',
    setValue: (m, val) => ({
      ...m,
      manufacturer_id: parseInt(val as string) || 0,
    }),
    isRequired: false,
  };
}
